import { useQueryClient } from '@tanstack/react-query';
import * as R from 'ramda';
import { useCallback, useEffect, useRef } from 'react';
import { DataQueryKey } from '../../../../hooks/useDataQuery/dataQuery.types';
import { getSuggestedProductsKey } from '../../../../services/modules/products/products.keys';
import useOrderBookStore from '../../../../stores/OrderBook/orderBook.store';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import useOrderBook from '../../../order-book/hooks/useOrderBook';
import { SavedStackItem } from '../../../orders/interfaces/use-order-item-stack.types';
import { invalidateAffectedOrderProductsGridQueries } from '../../../orders/utils/orderCalculate.utils';
import useProductDetailsActions from './useProductsDetailsActions';

const useProductDetailsRefetch = (productId?: string) => {
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();

  const { getProductDetailsSummaryKey } = useProductDetailsActions();
  const { allOrders, customerIds } = useOrderBook();

  const reloadOrderItemsQuantity = useOrderBookStore((state) => state.reloadOrderItemsQuantity);
  const selectedOrderId = useOrderBookStore((state) => state.selectedOrder?.id);
  const orders = useOrderBookStore((state) => state.orders);

  const orderBookOrderIds = orders?.length ? orders.map((order) => order.id) : undefined;

  const refetchSuggestedProducts = useCallback(async () => {
    if (!productId) {
      return;
    }

    const hasSuggestedOrderInOrderBook = allOrders.find((order) => order.status === 'SUGGESTED');
    if (!hasSuggestedOrderInOrderBook) {
      return;
    }

    const suggestedProductsGridQueryKey = [
      team?.id,
      ...getSuggestedProductsKey({ customerIds, productIds: [productId] }),
    ];
    await queryClient.refetchQueries({
      queryKey: suggestedProductsGridQueryKey,
    });
  }, [allOrders, customerIds, queryClient, team?.id, productId]);

  const reloadOrderItemsQuantityWithRefetch = useCallback(
    async (orderId: string, isSelectedOrder: boolean) => {
      const affectedOrderProducts = await reloadOrderItemsQuantity(true, orderId, isSelectedOrder);

      if (affectedOrderProducts?.length) {
        invalidateAffectedOrderProductsGridQueries({
          orderId,
          queryClient,
          teamId: team?.id,
          isOfflineMode: false,
          affectedOrderProducts,
        });
      }
    },
    [queryClient, reloadOrderItemsQuantity, team],
  );

  const updateProductRelations = useCallback(
    async (orderIds: string[]) => {
      await queryClient.refetchQueries({
        type: 'active',
        queryKey: [team?.id, DataQueryKey.ORDER_CALCULATE_SUMMARY],
      });
      await refetchSuggestedProducts();

      const promises = orderIds.map(async (orderId) => {
        await queryClient.refetchQueries({ queryKey: getProductDetailsSummaryKey(orderId) });
        const isSelectedOrder = selectedOrderId === orderId;

        if (isSelectedOrder) {
          await reloadOrderItemsQuantityWithRefetch(orderId, true);
        } else if (orderBookOrderIds?.includes(orderId)) {
          await reloadOrderItemsQuantityWithRefetch(orderId, false);
        }
      });

      await Promise.all(promises);
    },
    [
      getProductDetailsSummaryKey,
      orderBookOrderIds,
      queryClient.refetchQueries,
      team?.id,
      refetchSuggestedProducts,
      reloadOrderItemsQuantityWithRefetch,
      selectedOrderId,
    ],
  );

  const updateProductRelationsRef = useRef(updateProductRelations);
  useEffect(() => {
    updateProductRelationsRef.current = updateProductRelations;
  }, [updateProductRelations]);

  const refetchUsingOrderIds = useCallback((orderIds: string[]) => {
    if (!orderIds.length) {
      return;
    }

    return updateProductRelationsRef.current(orderIds);
  }, []);

  const refetchUsingStack = useCallback((savedStack: SavedStackItem[]) => {
    if (!savedStack.length) {
      return;
    }

    const orderIds = R.uniq(savedStack.map(({ orderId }) => orderId));
    return updateProductRelationsRef.current(orderIds);
  }, []);

  return { refetchUsingStack, refetchUsingOrderIds };
};

export default useProductDetailsRefetch;
